// Draws a visual's EVENTS on a Plotly time-series chart — the ONE
// implementation every chart type that supports events goes through:
// the vendored LineChart / ColumnLineChart (via BaseVisual._applyEventOverlay,
// which reaches this through window.__twastaEventOverlay — vendored files
// cannot import) and the native ComboChart (direct import).
//
// The x axis of these charts is a CATEGORY axis of formatted labels, so an
// event is SNAPPED to the bucket it falls in: the category whose start is the
// latest one on or before the event's date. A single-day event becomes a
// dashed line + a flag; one with an end date shades the buckets it spans.
// Several events in one bucket of one layer merge into one flag ("3 events")
// whose hover lists them all. An event inside a GAP of the data (no rows
// between two dates) is drawn in the gap, not on the last plotted date. The
// flags are annotations with hover text, and one chip in a
// small strip toggles the layer (its flags and its shapes).

import { toMillis, isoOf, type EventItem, type EventLayer, type EventsBlock } from './model'

export interface AxisEntry {
  label: string
  start: number
}

export interface OverlayInput {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  traces: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layout: any
  events: EventsBlock
  /** The chart's rows (column layers read their events from these). */
  rows?: Array<Record<string, unknown>>
  /** The X (Rows) field and the Events-zone field. */
  rowField?: string | null
  eventsField?: string | null
  /** [raw value, the label the chart draws for it] for every row. */
  rowLabels?: Array<[unknown, string]>
  /** Removes a drill affordance from a tick label (" [+]"). */
  stripLabel?: (label: string) => string
  /** The vendored date hierarchy, when it is on. */
  hierarchy?: { level: 'year' | 'month' | 'day'; filterYear: number | null; filterMonth: number | null } | null
  /** Layer visibility the viewer chose (kept across re-renders). */
  hidden?: Record<string, boolean>
  dark?: boolean
}

export interface OverlayLayerState {
  id: string
  name: string
  color: string
  kind: EventLayer['kind']
  count: number
  outside: number
  error?: string
  canAdd: boolean
  visible: boolean
}

export interface OverlayResult {
  axis: AxisEntry[]
  layers: OverlayLayerState[]
  notices: string[]
  traceIndexes: Record<string, number>
  shapeIndexes: Record<string, number[]>
  annotationIndexes: Record<string, number[]>
  notesLayer: EventLayer | null
  ref: EventsBlock['ref']
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY = 86400000

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  if (Number.isNaN(n)) return `rgba(71,85,105,${a})`
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

function esc(s: string): string {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const MONTHS_FMT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function displayDate(iso: string): string {
  const t = toMillis(iso)
  if (t == null) return iso || ''
  const d = new Date(t)
  const base = `${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS_FMT[d.getUTCMonth()]}-${String(d.getUTCFullYear()).slice(-2)}`
  if (!d.getUTCHours() && !d.getUTCMinutes()) return base
  return `${base} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}

/** The categories in the order the chart draws them, each with its start. */
export function buildAxis(input: OverlayInput): AxisEntry[] {
  const seen = new Set<string>()
  const cats: string[] = []
  for (const t of input.traces || []) {
    if (!t || t.meta?.twastaEvent) continue
    if (t.yaxis === 'y3') continue
    for (const x of (Array.isArray(t.x) ? t.x : [])) {
      const s = String(x)
      if (!seen.has(s)) { seen.add(s); cats.push(s) }
    }
  }
  const strip = input.stripLabel ?? ((s: string) => s)
  const h = input.hierarchy
  if (h) {
    return cats.map((label) => {
      const key = strip(label).trim()
      let start = NaN
      if (h.level === 'year') start = Date.UTC(Number(key), 0, 1)
      else if (h.level === 'month' && h.filterYear != null) {
        const m = MONTH_SHORT.indexOf(key)
        if (m >= 0) start = Date.UTC(h.filterYear, m, 1)
      } else if (h.level === 'day' && h.filterYear != null && h.filterMonth != null) {
        start = Date.UTC(h.filterYear, h.filterMonth, Number(key))
      }
      return { label, start }
    }).filter((a) => Number.isFinite(a.start))
  }
  const firstByLabel = new Map<string, number>()
  for (const [raw, lab] of input.rowLabels ?? []) {
    const t = toMillis(raw instanceof Date ? raw : (raw == null ? null : String(raw)))
    if (t == null) continue
    const prev = firstByLabel.get(lab)
    if (prev == null || t < prev) firstByLabel.set(lab, t)
  }
  return cats
    .map((label) => ({ label, start: firstByLabel.get(label) ?? NaN }))
    .filter((a) => Number.isFinite(a.start))
}

/** Where a time lands on the category axis.
 *
 *  The bucket it belongs to is the category whose start is the latest on or
 *  before it — when it is within one typical step (the median gap) of that
 *  start: a monthly chart puts the 20th on its month. Further than that, the
 *  chart has a GAP there (no rows between two dates), and the event is drawn
 *  IN the gap, as far along it as its date is, rather than pretending it
 *  happened on the last plotted date. -1 / null: outside the axis. */
function makeSnapper(axis: AxisEntry[]) {
  const sorted = axis.map((a, i) => ({ ...a, i })).sort((a, b) => a.start - b.start)
  const gaps = sorted.slice(1).map((b, k) => b.start - sorted[k].start).sort((a, b) => a - b)
  const step = gaps.length ? Math.max(gaps[Math.floor((gaps.length - 1) / 2)], 1) : DAY
  const end = sorted.length ? sorted[sorted.length - 1].start + step : 0
  const rank = (t: number): number => {
    if (!sorted.length || t >= end || t < sorted[0].start) return -1
    let lo = 0, hi = sorted.length - 1
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1
      if (sorted[mid].start <= t) lo = mid
      else hi = mid - 1
    }
    return lo
  }
  /** Category index of the bucket (no gap handling) — kept for band ends. */
  const snap = (t: number): number => { const r = rank(t); return r < 0 ? -1 : sorted[r].i }
  /** Axis position (a category index, or a half-way point inside a gap). */
  const place = (t: number): { pos: number; onBucket: boolean } | null => {
    const r = rank(t)
    if (r < 0) return null
    const here = sorted[r]
    const next = sorted[r + 1]
    if (!next || t - here.start < step) return { pos: here.i, onBucket: true }
    // In the gap: as far along it as the date is, kept clear of both points.
    const frac = Math.min(0.85, Math.max(0.15, (t - here.start) / (next.start - here.start)))
    return { pos: here.i + (next.i - here.i) * frac, onBucket: false }
  }
  const lastIdx = sorted.length ? sorted[sorted.length - 1].i : -1
  return { snap, place, end, lastIdx, first: sorted.length ? sorted[0].start : 0 }
}

/** A column layer's events: every row whose Events field has a value. */
function columnItems(input: OverlayInput, layer: EventLayer): EventItem[] {
  const field = layer.column || input.eventsField || ''
  const dateField = input.rowField || ''
  if (!field || !dateField) return []
  const lowerF = field.toLowerCase()
  const lowerD = dateField.toLowerCase()
  const seen = new Set<string>()
  const out: EventItem[] = []
  for (const row of input.rows ?? []) {
    let val: unknown, dt: unknown
    for (const [k, v] of Object.entries(row)) {
      const kl = k.toLowerCase()
      if (kl === lowerF) val = v
      if (kl === lowerD) dt = v
    }
    const label = val == null ? '' : String(val).trim()
    if (!label) continue
    const t = toMillis(dt instanceof Date ? dt : (dt == null ? null : String(dt)))
    if (t == null) continue
    const key = `${t}|${label}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ date: isoOf(t), end: '', label, category: '', note: '' })
  }
  return out
}

function hoverHtml(layer: EventLayer, items: EventItem[]): string {
  const rows = items.slice(0, 8).map((it) => {
    const when = displayDate(it.date) + (it.end ? ' → ' + displayDate(it.end) : '')
    const sub = [layer.name, when].filter(Boolean).join(' · ')
    const extra = [it.category, it.note, it.by_name && layer.kind === 'notes' ? 'by ' + it.by_name : '']
      .filter(Boolean).join(' · ')
    return `<b>${esc(it.label)}</b><br>${esc(sub)}${extra ? '<br>' + esc(extra) : ''}`
  })
  if (items.length > 8) rows.push(`… and ${items.length - 8} more`)
  return rows.join('<br><br>')
}

/**
 * Mutates ``layout``: per layer, dashed lines / shaded bands (``shapes``) and
 * the flags as hoverable ``annotations`` (their ``hovertext`` is the tooltip —
 * an annotation, unlike a trace point, may sit BETWEEN two categories, which
 * is where an event inside a gap belongs). ``traces`` is read for the axis,
 * never changed. Returns what the chip strip and the notes UI need. Never
 * throws for bad data — an event that cannot be placed is counted outside.
 */
export function applyEventOverlay(input: OverlayInput): OverlayResult | null {
  const ev = input.events
  if (!ev || !Array.isArray(ev.layers) || !ev.layers.length) return null
  const axis = buildAxis(input)
  const notices = [...(ev.notices ?? [])]
  const hidden = input.hidden ?? {}
  const result: OverlayResult = {
    axis, layers: [], notices, traceIndexes: {}, shapeIndexes: {}, annotationIndexes: {},
    notesLayer: ev.layers.find((l) => l.kind === 'notes') ?? null, ref: ev.ref,
  }
  const snapper = makeSnapper(axis)
  const layout = input.layout
  layout.shapes = Array.isArray(layout.shapes) ? layout.shapes : []
  layout.annotations = Array.isArray(layout.annotations) ? layout.annotations : []
  const hoverlabel = { bgcolor: '#1b2330', bordercolor: '#1b2330', font: { color: '#ffffff', size: 13 }, align: 'left' }

  ev.layers.forEach((layer, li) => {
    const visible = hidden[layer.id] !== undefined ? !hidden[layer.id] : !layer.hidden
    const items = layer.kind === 'column' ? columnItems(input, layer) : (layer.items ?? [])
    let outside = Number(layer.outside || 0)
    const points = new Map<number, EventItem[]>()
    const bands: Array<{ x0: number; x1: number; it: EventItem }> = []
    for (const it of items) {
      const start = toMillis(it.date)
      if (start == null) continue
      const endT = toMillis(it.end)
      const asBand = layer.look === 'band' || (layer.look === 'auto' && endT != null && endT > start)
      if (asBand) {
        const e = endT != null && endT >= start ? endT : start
        if (!axis.length || e < snapper.first || start >= snapper.end) { outside += 1; continue }
        const a = snapper.place(Math.max(start, snapper.first))
        const b = e >= snapper.end ? { pos: snapper.lastIdx, onBucket: true } : snapper.place(e)
        if (!a || !b) { outside += 1; continue }
        const left = (p: { pos: number; onBucket: boolean }) => (p.onBucket ? p.pos - 0.5 : p.pos)
        const right = (p: { pos: number; onBucket: boolean }) => (p.onBucket ? p.pos + 0.5 : p.pos)
        let x0 = Math.min(left(a), left(b)), x1 = Math.max(right(a), right(b))
        if (x1 - x0 < 0.2) { const c = (x0 + x1) / 2; x0 = c - 0.1; x1 = c + 0.1 }
        bands.push({ x0, x1, it })
      } else {
        const p = snapper.place(start)
        if (!p) { outside += 1; continue }
        const arr = points.get(p.pos) ?? []
        arr.push(it)
        points.set(p.pos, arr)
      }
    }

    const name = `tw-ev:${layer.id}`
    const shapeIdx: number[] = []
    const annIdx: number[] = []
    for (const pos of points.keys()) {
      if (layer.look === 'flag') continue
      shapeIdx.push(layout.shapes.length)
      layout.shapes.push({
        type: 'line', xref: 'x', yref: 'paper', x0: pos, x1: pos, y0: 0, y1: 1,
        line: { color: layer.color, width: 1.5, dash: 'dash' },
        layer: 'below', name, visible,
      })
    }
    for (const b of bands) {
      shapeIdx.push(layout.shapes.length)
      layout.shapes.push({
        type: 'rect', xref: 'x', yref: 'paper', x0: b.x0, x1: b.x1, y0: 0, y1: 1,
        fillcolor: rgba(layer.color, 0.11), line: { width: 1, color: rgba(layer.color, 0.35) },
        layer: 'below', name, visible,
      })
    }
    result.shapeIndexes[layer.id] = shapeIdx

    // Flags: at the top for single dates (stacked per layer), at the bottom
    // of each band. "Always" labels write the text beside the mark.
    const always = layer.labels === 'always'
    const topY = 1 - li * 0.06
    const bandY = 0.01 + li * 0.06
    for (const [pos, arr] of [...points.entries()].sort((a, b) => a[0] - b[0])) {
      const label = arr.length > 1 ? `${arr.length} events` : arr[0].label
      annIdx.push(layout.annotations.length)
      layout.annotations.push({
        xref: 'x', yref: 'paper', x: pos, y: topY, yanchor: 'top', xanchor: always ? 'left' : 'center',
        showarrow: false, text: always || arr.length > 1 ? `▼ ${esc(label)}` : '▼',
        font: { color: layer.color, size: 13 }, hovertext: hoverHtml(layer, arr), hoverlabel,
        bgcolor: always ? 'rgba(255,255,255,0.85)' : undefined, borderpad: 1,
        // captureevents (on by default with hovertext) is what shows the tooltip.
        name, visible,
      })
    }
    for (const b of bands) {
      annIdx.push(layout.annotations.length)
      layout.annotations.push({
        xref: 'x', yref: 'paper', x: b.x0, y: bandY, yanchor: 'bottom', xanchor: 'left',
        showarrow: false, text: always ? `■ ${esc(b.it.label)}` : '■',
        font: { color: layer.color, size: 13 }, hovertext: hoverHtml(layer, [b.it]), hoverlabel,
        bgcolor: always ? 'rgba(255,255,255,0.85)' : undefined, borderpad: 1,
        // captureevents (on by default with hovertext) is what shows the tooltip.
        name, visible,
      })
    }
    result.annotationIndexes[layer.id] = annIdx

    const count = [...points.values()].reduce((n, a) => n + a.length, 0) + bands.length
    result.layers.push({
      id: layer.id, name: layer.name, color: layer.color, kind: layer.kind,
      count, outside, error: layer.error, canAdd: layer.kind === 'notes' && !!layer.can_add, visible,
    })
  })

  const outsideTotal = result.layers.reduce((n, l) => n + (l.outside || 0), 0)
  if (outsideTotal > 0) {
    notices.push(`${outsideTotal} ${outsideTotal === 1 ? 'event is' : 'events are'} outside this chart's dates and not shown.`)
  }
  return result
}

// ── the chip strip (vendored charts and the native combo) ────────────────

export interface MountHooks {
  /** Open the notes panel (InsightVisual renders it). */
  openNotes?: (anchor: DOMRect) => void
}

/**
 * Draws the layer chips above the plot: one per layer (click = hide/show its
 * flags and shapes), "+ Note" when the caller may add notes, and an ⓘ with
 * the notices. Re-mounting replaces the previous strip.
 */
export function mountEventStrip(
  container: HTMLElement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gd: any,
  res: OverlayResult,
  hidden: Record<string, boolean>,
  hooks: MountHooks = {},
): void {
  container.querySelectorAll('.tw-ev-strip').forEach((n) => n.remove())
  if (!res.layers.length) return
  if (getComputedStyle(container).position === 'static') container.style.position = 'relative'
  const strip = document.createElement('div')
  strip.className = 'tw-ev-strip'
  strip.setAttribute('data-tw', 'visual-events')
  strip.style.cssText = [
    'position:absolute', 'left:8px', 'top:4px', 'z-index:5', 'display:flex', 'flex-wrap:wrap',
    'gap:6px', 'align-items:center', 'max-width:calc(100% - 64px)',
    'font-family:inherit', 'font-size:13px', 'pointer-events:auto',
  ].join(';')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const P = (typeof window !== 'undefined' ? (window as any).Plotly : null)

  const setVisible = (layerId: string, on: boolean) => {
    hidden[layerId] = !on
    try {
      const ti = res.traceIndexes[layerId]
      if (P && ti != null) P.restyle(gd, { visible: on }, [ti])
      const upd: Record<string, boolean> = {}
      for (const si of res.shapeIndexes[layerId] ?? []) upd[`shapes[${si}].visible`] = on
      for (const ai of res.annotationIndexes[layerId] ?? []) upd[`annotations[${ai}].visible`] = on
      if (P && Object.keys(upd).length) P.relayout(gd, upd)
    } catch (e) {
      console.warn('[visual-events] toggle failed', e)
    }
  }

  for (const l of res.layers) {
    const chip = document.createElement('button')
    chip.type = 'button'
    chip.className = 'tw-ev-chip'
    chip.setAttribute('data-tw', `visual-event-layer:${l.id}`)
    chip.setAttribute('aria-pressed', String(l.visible))
    const paint = (on: boolean) => {
      chip.style.cssText = [
        'display:inline-flex', 'align-items:center', 'gap:6px', 'border-radius:999px',
        'padding:2px 10px', 'min-height:26px', 'font:inherit', 'font-size:13px', 'cursor:pointer',
        'border:1px solid var(--color-border, #dfe3ea)',
        'background:var(--color-bg-secondary, #ffffff)', 'color:var(--color-text, #1b2330)',
        `opacity:${on ? 1 : 0.5}`,
      ].join(';')
      chip.setAttribute('aria-pressed', String(on))
    }
    paint(l.visible)
    const sw = document.createElement('span')
    sw.style.cssText = `width:10px;height:10px;border-radius:3px;background:${l.color};flex-shrink:0`
    chip.appendChild(sw)
    chip.appendChild(document.createTextNode(l.name))
    if (l.error) {
      chip.title = l.error
      const warn = document.createElement('span')
      warn.textContent = ' ⚠'
      warn.style.color = 'var(--color-warning, #b54708)'
      chip.appendChild(warn)
    } else {
      chip.title = `${l.count} on this chart${l.outside ? ` · ${l.outside} outside` : ''} — click to ${l.visible ? 'hide' : 'show'}`
    }
    let on = l.visible
    chip.addEventListener('click', (e) => {
      e.stopPropagation()
      on = !on
      paint(on)
      setVisible(l.id, on)
    })
    strip.appendChild(chip)
  }

  const notes = res.layers.find((l) => l.kind === 'notes')
  if (notes && hooks.openNotes) {
    const add = document.createElement('button')
    add.type = 'button'
    add.setAttribute('data-tw', 'visual-event-notes')
    add.textContent = notes.canAdd ? '+ Note' : 'Notes'
    add.style.cssText = [
      'display:inline-flex', 'align-items:center', 'border-radius:999px', 'padding:2px 10px',
      'min-height:26px', 'font:inherit', 'font-size:13px', 'font-weight:600', 'cursor:pointer',
      'border:1px solid var(--color-accent, #0061D0)', 'background:var(--color-accent, #0061D0)',
      'color:#ffffff',
    ].join(';')
    add.addEventListener('click', (e) => {
      e.stopPropagation()
      hooks.openNotes?.(add.getBoundingClientRect())
    })
    strip.appendChild(add)
  }

  if (res.notices.length) {
    const info = document.createElement('span')
    info.setAttribute('role', 'note')
    info.textContent = 'ⓘ'
    info.title = res.notices.join('\n')
    info.setAttribute('aria-label', res.notices.join(' '))
    info.style.cssText = 'cursor:help;color:var(--color-text-secondary, #465062);padding:0 4px'
    strip.appendChild(info)
  }
  container.appendChild(strip)
}

/** The registration the vendored BaseVisual reaches (it cannot import). */
export interface EventOverlayApi {
  apply: (input: OverlayInput) => OverlayResult | null
  mount: typeof mountEventStrip
}

export function registerEventOverlay(): void {
  if (typeof window === 'undefined') return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  // Always the latest module's functions (a hot reload replaces them).
  const api: EventOverlayApi = { apply: applyEventOverlay, mount: mountEventStrip }
  w.__twastaEventOverlay = api
}
